import React, { useState } from 'react';
import OtpInput from './OtpInput';
import api from '../api';

// Explicit steps — no ambiguous ternary logic
const S = { MANAGE: 'manage', ENTER: 'enter', CONFIRM: 'confirm' };

export default function PinSetupModal({ onClose, existingPin, onPinChanged }) {
  // Start at MANAGE if PIN already exists, ENTER if setting for the first time
  const [step, setStep]       = useState(existingPin ? S.MANAGE : S.ENTER);
  const [pin, setPin]         = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const reset = () => { setPin(''); setConfirm(''); setError(''); };

  // ── Remove PIN ─────────────────────────────────────────────────────────────
  const handleRemove = async () => {
    setLoading(true); setError('');
    try {
      await api.delete('/user/pin');
      localStorage.removeItem('lxops_has_pin');
      localStorage.removeItem('lxops_pin_email');
      onPinChanged(false);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove PIN. Try again.');
    } finally { setLoading(false); }
  };

  // ── Enter step → move to Confirm ──────────────────────────────────────────
  const handleEnter = (e) => {
    e.preventDefault();
    if (pin.length !== 6) { setError('Enter a full 6-digit PIN.'); return; }
    setError('');
    setConfirm('');
    setStep(S.CONFIRM);
  };

  // ── Confirm step → save PIN ───────────────────────────────────────────────
  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirm.length !== 6) { setError('Enter the PIN again to confirm.'); return; }
    if (confirm !== pin) {
      setError('PINs do not match. Please start again.');
      reset();
      setStep(S.ENTER);
      return;
    }
    setLoading(true); setError('');
    try {
      await api.post('/user/pin', { pin: String(pin) });
      const userData = JSON.parse(localStorage.getItem('lxops_user') || '{}');
      localStorage.setItem('lxops_has_pin', 'true');
      localStorage.setItem('lxops_pin_email', userData.email || '');
      onPinChanged(true);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save PIN. Try again.');
      reset();
      setStep(S.ENTER);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-sm rounded-2xl p-6 animate-fade-in"
        style={{ backgroundColor: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-400 transition-colors text-lg">
          ✕
        </button>

        {/* ── MANAGE: existing PIN options ── */}
        {step === S.MANAGE && (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-indigo-950/60 border border-indigo-800/50 rounded-full
                flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🔐</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">PIN Login</h3>
              <p className="text-gray-400 text-sm">You have a PIN set for quick sign-in.</p>
            </div>

            {error && <p className="text-red-400 text-sm text-center mb-4">{error}</p>}

            <div className="space-y-3">
              <button
                onClick={() => { reset(); setStep(S.ENTER); }}
                disabled={loading}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition-colors btn-primary"
              >
                Change PIN
              </button>
              <button
                onClick={handleRemove}
                disabled={loading}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition-colors"
                style={{
                  backgroundColor: 'rgba(127,29,29,0.3)',
                  border: '1px solid rgba(185,28,28,0.5)',
                  color: '#fca5a5',
                }}
              >
                {loading ? 'Removing…' : 'Remove PIN'}
              </button>
            </div>
          </>
        )}

        {/* ── ENTER: type new PIN ── */}
        {step === S.ENTER && (
          <form onSubmit={handleEnter} className="space-y-5">
            <div className="text-center">
              <h3 className="text-lg font-bold text-white mb-1">
                {existingPin ? 'Change PIN' : 'Set up PIN login'}
              </h3>
              <p className="text-gray-400 text-sm">
                Choose a 6-digit PIN. It's hashed and never stored in plain text.
              </p>
            </div>

            <p className="text-xs text-gray-500 text-center">Enter PIN</p>
            <OtpInput key="enter" length={6} onChange={setPin} disabled={loading} />

            {error && <p className="text-red-400 text-xs text-center">{error}</p>}

            <button type="submit" disabled={loading || pin.length !== 6} className="btn-primary w-full">
              Next →
            </button>

            {existingPin && (
              <button type="button"
                onClick={() => { reset(); setStep(S.MANAGE); }}
                className="w-full text-sm text-gray-500 hover:text-gray-300 transition-colors">
                ← Back
              </button>
            )}
          </form>
        )}

        {/* ── CONFIRM: re-enter PIN ── */}
        {step === S.CONFIRM && (
          <form onSubmit={handleConfirm} className="space-y-5">
            <div className="text-center">
              <h3 className="text-lg font-bold text-white mb-1">Confirm PIN</h3>
              <p className="text-gray-400 text-sm">Re-enter your PIN to confirm.</p>
            </div>

            <p className="text-xs text-gray-500 text-center">Confirm PIN</p>
            <OtpInput key="confirm" length={6} onChange={setConfirm} disabled={loading} />

            {error && <p className="text-red-400 text-xs text-center">{error}</p>}

            <button type="submit" disabled={loading || confirm.length !== 6} className="btn-primary w-full">
              {loading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                : 'Confirm & Save PIN'}
            </button>

            <button type="button"
              onClick={() => { reset(); setStep(S.ENTER); }}
              className="w-full text-sm text-gray-500 hover:text-gray-300 transition-colors">
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
